
import React, { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input";
import { GOOGLE_MAPS_API_KEY } from '@/config/api';
import { Loader } from 'lucide-react';

interface AddressAutocompleteProps {
  onAddressSelect: (addressData: {
    address: string;
    street: string;
    street_number: string;
    city: string;
    zipCode: string;
    country: string;
  }) => void;
  defaultValue?: string;
  className?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  defaultValue = '',
  className
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!document.querySelector(`script[src^="https://maps.googleapis.com/maps/api/js"]`)) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setScriptLoaded(true);
      };
      document.head.appendChild(script);
    } else {
      setScriptLoaded(true);
    }

    return () => {
      // Clean up event listeners if needed
    };
  }, []);

  useEffect(() => {
    if (scriptLoaded && inputRef.current) {
      const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        fields: ['address_component', 'formatted_address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place && place.address_components) {
          setIsLoading(true);
          
          let streetNumber = '';
          let street = '';
          let city = '';
          let zipCode = '';
          let country = '';
          
          // Extract address components
          for (const component of place.address_components) {
            const types = component.types;
            
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            } else if (types.includes('route')) {
              street = component.long_name;
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('postal_code')) {
              zipCode = component.long_name;
            } else if (types.includes('country')) {
              country = component.short_name;
            }
          }
          
          // Set the input value to the formatted address
          if (place.formatted_address) {
            setInputValue(place.formatted_address);
          }
          
          onAddressSelect({
            address: place.formatted_address || '',
            street,
            street_number: streetNumber,
            city,
            zipCode,
            country,
          });
          
          setIsLoading(false);
        }
      });
    }
  }, [scriptLoaded, onAddressSelect]);
  
  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Enter your address"
        className={className}
      />
      {isLoading && (
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
